import { computed, ref } from "@vue/composition-api";

export type MinMaxParam = {
  type: "number";
  description: string;
  minimum: number;
  maximum: number;
};

export type ParamType = MinMaxParam;

type PropCheckFn = (value: string) => { has_err: boolean; err_msg: string };

export const UseParamsSchemeChecker = (
  properties: Record<string, ParamType>
) => {
  const props_status = ref(
    Object.keys(properties).reduce((acc, key) => {
      acc[key] = { has_err: false, err_msg: "" };
      return acc;
    }, {} as Record<string, { has_err: boolean; err_msg: string }>)
  );

  const props = Object.entries(properties).reduce((acc, [key, val]) => {
    const fn_acc: PropCheckFn[] = [];
    if ("minimum" in val) {
      fn_acc.push((value: string) => {
        const has_err = Number(value) < val.minimum;
        return {
          has_err,
          err_msg: `The value must be greater than or equal to ${val.minimum}`,
        };
      });
    }
    if ("maximum" in val) {
      fn_acc.push((value: string) => {
        const has_err = Number(value) > val.maximum;
        return {
          has_err,
          err_msg: `The value must be less than or equal to ${val.maximum}`,
        };
      });
    }
    acc[key] = (value: string) => {
      const result = fn_acc.map((fn) => fn(value));
      const has_err = result.some((r) => r.has_err);
      const err_msg = result
        .filter((r) => r.has_err)
        .map((r) => r.err_msg)
        .join(", ");
      return { has_err, err_msg };
    };
    return acc;
  }, {} as Record<string, (v: string) => { has_err: boolean; err_msg: string }>);

  const update = (key: string, value: string) => {
    props_status.value[key] = props[key](value);
  };

  const is_valid = computed(() => {
    return Object.keys(props_status.value).every(
      (key) => !props_status.value[key].has_err
    );
  });

  return {
    update,
    props_status,
    is_valid,
  };
};
